import { initVersionedSchema, toTextContent } from '@principle-theorem/editor';
import {
  SkippedDestinationEntityRecord,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityJobRunOptions,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
  type ITranslationMap,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  Event,
  FollowUp,
  Interaction,
  TreatmentPlan,
  TreatmentStep,
  stafferToNamedDoc,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  AppointmentStatus,
  EventType,
  IFollowUp,
  isTag,
  ITag,
  ITreatmentCategory,
  ITreatmentPlan,
  ParticipantType,
  TreatmentPlanStatus,
  TreatmentStepStatus,
  type IAssociatedTreatment,
  type IEvent,
  type IInteractionV2,
  type IPatient,
  type IPractice,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  HISTORY_DATE_FORMAT,
  INamedDocument,
  ITimePeriod,
  WithPrimaryKey,
  getError,
  initFirestoreModel,
  isINamedDocument,
  isSameRef,
  resolveSequentially,
  snapshotCombineLatest,
  toMoment,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  type DocumentReference,
  type ISODateType,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, first, uniqBy } from 'lodash';
import * as moment from 'moment-timezone';
import { Moment } from 'moment-timezone';
import { combineLatest, from, of, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientAppointmentDestinationEntity,
  IPatientAppointmentBuildData,
  PATIENT_APPOINTMENT_RESOURCE_TYPE,
  type IPatientAppointmentJobData,
  type IPatientAppointmentMigrationData,
} from '../../../destination/entities/patient-appointments';
import {
  IStepWithIdentifiers,
  PATIENT_TREATMENT_PLAN_CUSTOM_MAPPING_TYPE,
  findTreatmentStepForAppointment,
} from '../../../destination/entities/patient-treatment-plans';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { APPOINTMENT_BOOK_TO_PRACTITIONER_CUSTOM_MAPPING_TYPE } from '../../../mappings/appointment-book-to-practitioner';
import { getPractitionerOrDefault } from '../../../mappings/staff';
import { PracticeMigration } from '../../../practice-migrations';
import { type TranslationMapHandler } from '../../../translation-map';
import {
  OasisPatientAppointment,
  PatientAppointmentSourceEntity,
} from '../../source/entities/patient-appointments';
import { PatientTreatmentsSourceEntity } from '../../source/entities/patient-treatments';
import {
  OasisPatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { OasisAppointmentBookToPractitionerMappingHandler } from '../mappings/appointment-book-to-practitioner';
import { OasisAppointmentBookToStatusMappingHandler } from '../mappings/appointment-book-to-status';
import { OasisPracticeMappingHandler } from '../mappings/practices';
import { OasisStafferMappingHandler } from '../mappings/staff';
import { OasisAppointmentStepSizeMappingHandler } from '../mappings/appointment-step-size';
import {
  OASIS_PLAN_NAME,
  OasisTreatmentPlanDataBuilder,
  PatientTreatmentPlanDestinationEntity,
} from './patient-treatment-plans';
import { PatientDestinationEntity } from './patients';
import { StafferDestinationEntity } from './staff';
import { AppointmentCategoryToTreatmentCategoryMappingHandler } from '../mappings/appointment-category-to-treatment-category';
import { AppointmentCategoryDestination } from '../../../mappings/appointment-to-treatment-category-to-xlsx';
import {
  AppointmentBookToAvailableStatus,
  BOOK_STATUS_TO_APPOINTMENT_STATUS,
} from '../../../mappings/appointment-book-to-status';

export const PATIENT_APPOINTMENT_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_APPOINTMENT_RESOURCE_TYPE,
    label: 'Patient Appointments',
    description: '',
  },
});

interface IJobData extends IPatientAppointmentJobData<OasisPatient> {
  practices: WithPrimaryKey<ITranslationMap<IPractice>>[];
  appointmentBookToPractitioner: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  appointmentBookToStatus: WithPrimaryKey<
    ITranslationMap<object, AppointmentBookToAvailableStatus>
  >[];
  appointmentToTreatmentCategory: WithPrimaryKey<
    ITranslationMap<AppointmentCategoryDestination>
  >[];
}

export class PatientAppointmentDestinationEntity extends BasePatientAppointmentDestinationEntity<
  OasisPatient,
  IJobData
> {
  destinationEntity = PATIENT_APPOINTMENT_DESTINATION_ENTITY;
  patientSourceEntity = new PatientSourceEntity();
  treatmentPlanName = OASIS_PLAN_NAME;

  override canMigrateByDateRange = true;
  override canMigrateByIdRange = true;

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    appointments: new PatientAppointmentSourceEntity(),
    treatments: new PatientTreatmentsSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
    treatmentPlans: new PatientTreatmentPlanDestinationEntity(),
  };

  customMappings = {
    staff: new OasisStafferMappingHandler(),
    practices: new OasisPracticeMappingHandler(),
    appointmentBookToPractitioner:
      new OasisAppointmentBookToPractitionerMappingHandler(),
    appointmentBookToStatus: new OasisAppointmentBookToStatusMappingHandler(),
    appointmentToTreatmentCategory:
      new AppointmentCategoryToTreatmentCategoryMappingHandler(),
    appointmentStepSize: new OasisAppointmentStepSizeMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IJobData[]> {
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const practitioners$ = from(brand$).pipe(
      switchMap((brand) => Firestore.getDocs(Brand.stafferCol(brand))),
      toINamedDocuments()
    );
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));

    return snapshotCombineLatest([
      staff$,
      practitioners$,
      brand$,
      this.customMappings.practices.getRecords(translationMap),
      this.customMappings.appointmentBookToPractitioner.getRecords(
        translationMap
      ),
      this.customMappings.appointmentBookToStatus.getRecords(translationMap),
      this.customMappings.appointmentToTreatmentCategory.getRecords(
        translationMap
      ),
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [
            staff,
            practitioners,
            brand,
            practices,
            appointmentBookToPractitioner,
            appointmentBookToStatus,
            appointmentToTreatmentCategory,
          ],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            sourcePatient,
            staff,
            practitioners,
            brand,
            practices,
            appointmentBookToPractitioner,
            appointmentBookToStatus,
            appointmentToTreatmentCategory,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IJobData
  ): Promise<
    | IPatientAppointmentMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = this.sourceEntities.patients.getSourceRecordId(
        data.sourcePatient.data
      );

      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId.toString(),
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient,
          `No patient with id ${sourcePatientId}`
        );
      }

      const patient = await Firestore.getDoc(patientRef);

      const sourceAppointments =
        await this.sourceEntities.appointments.filterRecords(
          migration,
          'patientId',
          data.sourcePatient.data.id
        );

      const planUid =
        OasisTreatmentPlanDataBuilder.getPlanIdentifier(sourcePatientId);
      const planDestinationRef =
        await translationMap.getDestination<ITreatmentPlan>(
          planUid,
          PATIENT_TREATMENT_PLAN_CUSTOM_MAPPING_TYPE
        );

      const resolvedPlan = planDestinationRef
        ? await Firestore.safeGetDoc(planDestinationRef)
        : undefined;

      const plan =
        resolvedPlan ??
        TreatmentPlan.init({
          name: OASIS_PLAN_NAME,
          status: TreatmentPlanStatus.InProgress,
        });

      const steps: IStepWithIdentifiers[] = [];

      const appointments: (IPatientAppointmentBuildData | undefined)[] =
        await resolveSequentially(
          sourceAppointments,
          async (sourceAppointment) => {
            const appointmentUid = this.sourceEntities.appointments
              .getSourceRecordId(sourceAppointment.data)
              .toString();

            const appointmentBookId =
              sourceAppointment.data.appointmentPractitionerId;

            const practitionerMap = await this.resolvePractitioner(
              data,
              appointmentBookId,
              translationMap
            );

            if (!practitionerMap) {
              throw new Error(
                `No practitioner found for appointment book ${appointmentBookId}`
              );
            }

            const practitioner = data.practitioners.find((searchPractitioner) =>
              isSameRef(searchPractitioner, practitionerMap)
            );

            if (!practitioner) {
              throw new Error(
                `Practitioner with ID ${appointmentBookId} not found in migration data`
              );
            }

            const practiceMap = first(data.practices);

            if (!practiceMap?.destinationIdentifier) {
              throw new Error('Missing practice');
            }

            const practice = await Firestore.getDoc(
              practiceMap.destinationIdentifier
            );

            const createdAt = sourceAppointment.record.translations.from;

            const [appointment, interactions, followUp] =
              await this._buildAppointmentData(
                migration,
                data,
                translationMap,
                sourcePatientId,
                patient,
                practitioner,
                sourceAppointment,
                sourceAppointments,
                practice,
                migration.configuration.backupDate
              );

            const stepUid =
              await OasisTreatmentPlanDataBuilder.getStepIdentifier(
                migration,
                this.sourceEntities.appointments,
                sourcePatientId,
                migration.configuration.timezone,
                undefined,
                sourceAppointment,
                sourceAppointments
              );

            const appointmentDate = toMomentTz(
              sourceAppointment.record.translations.from,
              migration.configuration.timezone
            ).format(HISTORY_DATE_FORMAT);

            const summary = sourceAppointment.data.treatmentSummary;
            const categoryDestinationIdentifier = summary?.length
              ? data.appointmentToTreatmentCategory.find(
                  (categoryMap) =>
                    categoryMap.sourceLabel?.trim() === summary.trim()
                )?.associatedValue
              : undefined;
            const categoryDestination = isINamedDocument(
              categoryDestinationIdentifier
            )
              ? await Firestore.getDoc(categoryDestinationIdentifier.ref)
              : undefined;
            const overrideTreatmentCategory = isTag(categoryDestination)
              ? undefined
              : (categoryDestination?.ref as DocumentReference<ITreatmentCategory>);
            if (appointment.treatmentPlan && overrideTreatmentCategory) {
              appointment.treatmentPlan.treatmentStep.display = {
                ...appointment.treatmentPlan.treatmentStep.display,
                overrideTreatmentCategory,
              };
            }

            const appointmentTag = isTag(categoryDestination)
              ? (categoryDestination as INamedDocument<ITag>)
              : undefined;
            if (appointmentTag) {
              appointment.tags.push(appointmentTag);
            }

            const name = summary
              ? `${sourceAppointment.data.treatmentSummary} - ${appointmentDate}`
              : appointmentDate;

            if (!appointment.treatmentPlan) {
              steps.push({
                ...TreatmentStep.init({
                  name,
                  status:
                    appointment.status === AppointmentStatus.Complete
                      ? TreatmentStepStatus.Complete
                      : TreatmentStepStatus.Incomplete,
                  schedulingRules: {
                    duration: appointment.event
                      ? Event.duration(appointment.event)
                      : 0,
                  },
                  display: {
                    overrideTreatmentCategory,
                  },
                }),
                sourceIdentifier: stepUid,
                createdAt,
                date: appointmentDate,
              });
            }

            return {
              sourcePatientId: sourcePatientId.toString(),
              patientRef,
              appointmentUid,
              createdAt,
              appointment,
              planUid,
              stepUid,
              interactions,
              followUp,
            };
          }
        );

      return {
        patientRef,
        sourcePatientId: sourcePatientId.toString(),
        appointments: compact(appointments),
        planPairs: [
          {
            plan: {
              ...plan,
              sourceIdentifier: planUid,
            },
            steps: uniqBy(steps, (step) => step.sourceIdentifier),
          },
        ],
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }

  async resolvePractitioner(
    data: IJobData,
    appointmentBookId: number,
    translationMap: TranslationMapHandler
  ): Promise<DocumentReference<IStaffer> | undefined> {
    const appointmentBookPractitionerMap =
      data.appointmentBookToPractitioner.find(
        (practitionerMap) =>
          practitionerMap.sourceIdentifier === appointmentBookId.toString()
      );

    if (!appointmentBookPractitionerMap) {
      // eslint-disable-next-line no-console
      console.error(
        'This appointment book ID does not exist adding blank mapping',
        appointmentBookId
      );
      await translationMap.upsert({
        sourceIdentifier: appointmentBookId.toString(),
        sourceLabel: '',
        resourceType: APPOINTMENT_BOOK_TO_PRACTITIONER_CUSTOM_MAPPING_TYPE,
      });
      return;
    }

    if (!appointmentBookPractitionerMap?.sourceLink?.sourceIdentifier) {
      return;
    }

    return getPractitionerOrDefault(
      appointmentBookPractitionerMap.sourceLink.sourceIdentifier,
      data.staff,
      data.practitioners
    )?.ref;
  }

  async resolveAppointmentPeriod(
    migration: WithRef<IPracticeMigration>,
    sourceAppointment: IGetRecordResponse<OasisPatientAppointment>,
    practice: WithRef<IPractice>
  ): Promise<{ from: Moment; to: Moment }> {
    const appointmentFrom = toMomentTz(
      sourceAppointment.record.translations.from,
      practice.settings.timezone
    );
    const durationMins =
      await this.customMappings.appointmentStepSize.getAppointmentDuration(
        migration,
        sourceAppointment.data
      );
    return { from: appointmentFrom, to: appointmentFrom.clone().add(durationMins, 'minutes') };
  }

  private async _buildAppointmentData(
    migration: WithRef<IPracticeMigration>,
    jobData: IJobData,
    translationMap: TranslationMapHandler,
    sourcePatientId: number,
    patient: WithRef<IPatient>,
    practitioner: INamedDocument<IStaffer>,
    sourceAppointment: IGetRecordResponse<OasisPatientAppointment>,
    patientAppointments: IGetRecordResponse<OasisPatientAppointment>[],
    practice: WithRef<IPractice>,
    backupDate: ISODateType
  ): Promise<
    [
      IPatientAppointmentBuildData['appointment'],
      IInteractionV2[],
      IFollowUp | undefined,
    ]
  > {
    const interactions = compact([sourceAppointment.data.notes]).map((note) => {
      const createdAt = toMoment(
        sourceAppointment.record.translations.createdAt
      ).isAfter(moment())
        ? toTimestamp()
        : sourceAppointment.record.translations.createdAt;

      return Interaction.init({
        uid: sourceAppointment.record.uid,
        title: [toTextContent(`Added a note`)],
        content: initVersionedSchema(note),
        createdAt,
        pinned: true,
      });
    });

    const { from: appointmentStartTime, to: appointmentEndTime } =
      await this.resolveAppointmentPeriod(
        migration,
        sourceAppointment,
        practice
      );

    const associatedPlan = await resolveAssociatedPlan(
      migration,
      this.sourceEntities.appointments,
      sourcePatientId,
      practice,
      translationMap,
      sourceAppointment,
      patientAppointments
    );

    const appointment: IPatientAppointmentBuildData['appointment'] = {
      event: getAppointmentEvent(
        {
          from: appointmentStartTime,
          to: appointmentEndTime,
        },
        patient,
        practice,
        practitioner
      ),
      eventHistory: [],
      status: this._determineStatus(
        sourceAppointment,
        appointmentStartTime,
        backupDate
      ),
      statusHistory: [],
      practice: toNamedDocument(practice),
      practitioner: stafferToNamedDoc(practitioner),
      cancellationHistory: [],
      dependencies: [],
      tags: [],
      ...initFirestoreModel(),
      treatmentPlan: associatedPlan,
    };

    const appointmentBookStatusMap = jobData.appointmentBookToStatus.find(
      (statusMap) =>
        statusMap.sourceIdentifier ===
        sourceAppointment.data.appointmentPractitionerId.toString()
    );
    if (!appointmentBookStatusMap?.destinationValue) {
      return [appointment, interactions, undefined];
    }

    const mappedStatus = appointmentBookStatusMap.destinationValue.value;
    appointment.status = BOOK_STATUS_TO_APPOINTMENT_STATUS[mappedStatus];

    const setFollowUp =
      mappedStatus === AppointmentBookToAvailableStatus.CancelWithFollowUp ||
      mappedStatus === AppointmentBookToAvailableStatus.UnscheduledWithFollowUp;
    const canSetFollowUp = appointmentStartTime.isAfter(
      toMomentTz(backupDate, migration.configuration.timezone),
      'day'
    );

    let followUp: IFollowUp | undefined;
    if (setFollowUp && canSetFollowUp) {
      const appointmentUid = this.sourceEntities.appointments
        .getSourceRecordId(sourceAppointment.data)
        .toString();
      followUp = FollowUp.init({
        uid: `${appointmentUid}-followUp`,
        createFollowUp: true,
        followUpDate: toTimestamp(appointmentStartTime),
      });
    }

    const removeEvent =
      mappedStatus === AppointmentBookToAvailableStatus.Unscheduled ||
      mappedStatus === AppointmentBookToAvailableStatus.UnscheduledWithFollowUp;
    if (removeEvent) {
      appointment.eventHistory.push({
        createdAt: toTimestamp(),
        event: appointment.event,
      });
      appointment.event = undefined;
    }

    return [appointment, interactions, followUp];
  }

  private _determineStatus(
    appointment: IGetRecordResponse<OasisPatientAppointment>,
    appointmentStartTime: Moment,
    backupDate: string
  ): AppointmentStatus {
    if (appointment.data.performedAt) {
      return AppointmentStatus.Complete;
    }

    if (appointmentStartTime.isBefore(toMoment(backupDate))) {
      return AppointmentStatus.Complete;
    }

    if (appointment.data.isConfirmed) {
      return AppointmentStatus.Confirmed;
    }

    return AppointmentStatus.Scheduled;
  }
}

export function getAppointmentEvent(
  period: ITimePeriod,
  patient?: WithRef<IPatient>,
  practice?: WithRef<IPractice>,
  practitioner?: INamedDocument<IStaffer> | WithRef<IStaffer>
): IEvent | undefined {
  if (!practice || !patient || !practitioner) {
    return;
  }
  return Event.init({
    from: toTimestamp(period.from),
    to: toTimestamp(period.to),
    practice: toNamedDocument(practice),
    type: EventType.Appointment,
    participants: [
      {
        ...toNamedDocument(patient),
        type: ParticipantType.Patient,
      },
      {
        ...stafferToNamedDoc(practitioner),
        type: ParticipantType.Staffer,
      },
    ],
  });
}

async function resolveAssociatedPlan(
  migration: WithRef<IPracticeMigration>,
  appointmentResolver: PatientAppointmentSourceEntity,
  sourcePatientId: number,
  practice: WithRef<IPractice>,
  translationMap: TranslationMapHandler,
  appointment: IGetRecordResponse<OasisPatientAppointment>,
  patientAppointments: IGetRecordResponse<OasisPatientAppointment>[]
): Promise<IAssociatedTreatment | undefined> {
  const stepUid = await OasisTreatmentPlanDataBuilder.getStepIdentifier(
    migration,
    appointmentResolver,
    sourcePatientId,
    practice.settings.timezone,
    undefined,
    appointment,
    patientAppointments
  );

  const stepRef = await findTreatmentStepForAppointment(
    translationMap,
    stepUid
  );

  if (!stepRef) {
    return;
  }

  const step = await Firestore.safeGetDoc(stepRef);
  if (!step) {
    return;
  }

  const plan = await TreatmentStep.treatmentPlan(step);

  return TreatmentPlan.treatmentStepToAssociatedTreatment(plan, step);
}
